import React, { useState, useMemo } from 'react';
import { useCRM } from '../../utils/CRMContext';
import { 
  TimberProject, 
  ProjectTask, 
  Milestone, 
  ProjectDepartment, 
  TaskStatus,
  TaskPriority,
  KitFrameStage,
  FrameType,
  DEPARTMENT_DISPLAY_LABELS,
  DEPARTMENT_SWATCH_COLORS,
} from '../../types';
import { Package, Home, ClipboardList, Settings, Truck, CheckCircle } from 'lucide-react';
import './ProjectBoard.css';

interface ProjectBoardProps {
  projects: TimberProject[];
  tasks: ProjectTask[];
  milestones: Milestone[];
  selectedProjectId: string | null;
  userId: string;
}

const DEPARTMENTS: ProjectDepartment[] = [
  'preconstruction',
  'design',
  'procurement',
  'production',
  'installation',
];
const KIT_STAGES: KitFrameStage[] = ['procurement', 'production', 'shipment', 'delivery'];
const TASK_STATUSES: TaskStatus[] = ['pending', 'in-progress', 'review', 'completed'];

const DEPARTMENT_COLORS = DEPARTMENT_SWATCH_COLORS;

const KIT_STAGE_COLORS: Record<KitFrameStage, string> = {
  procurement: '#4299E1', // Blue
  production: '#ED8936',  // Orange
  shipment: '#9F7AEA',    // Purple
  delivery: '#48BB78',    // Green
};

const PRIORITY_COLORS: Record<TaskPriority, string> = {
  low: '#48BB78',
  medium: '#4299E1',
  high: '#ED8936',
  urgent: '#E53E3E',
};

export const ProjectBoard: React.FC<ProjectBoardProps> = ({
  projects,
  tasks,
  milestones,
  selectedProjectId,
  userId,
}) => {
  const { createProjectTask, updateProjectTask, deleteProjectTask } = useCRM();
  const [viewBy, setViewBy] = useState<'department' | 'status' | 'kit-stage'>('department');
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState<ProjectTask | null>(null);

  // Get the selected project to determine frame type
  const selectedProject = useMemo(() => 
    projects.find(p => p.id === selectedProjectId),
    [projects, selectedProjectId]
  );

  // Determine if this is a kit frame project
  const isKitFrame = selectedProject?.frameType === 'kit';

  // Auto-switch to kit-stage view for kit frame projects
  React.useEffect(() => {
    if (isKitFrame && viewBy === 'department') {
      setViewBy('kit-stage');
    } else if (!isKitFrame && viewBy === 'kit-stage') {
      setViewBy('department');
    }
  }, [isKitFrame, selectedProjectId]);

  // Task form state - use different department for kit frames
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    department: 'design' as ProjectDepartment,
    kitStage: 'procurement' as KitFrameStage, // For kit frames
    priority: 'medium' as TaskPriority,
    assignedTo: [] as string[],
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  });

  // Filter tasks
  const filteredTasks = useMemo(() => {
    if (!selectedProjectId) return tasks;
    return tasks.filter(t => t.projectId === selectedProjectId);
  }, [tasks, selectedProjectId]);

  const getTasksByDepartment = (department: ProjectDepartment): ProjectTask[] => {
    return filteredTasks
      .filter(t => t.department === department)
      .sort((a, b) => {
        const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      });
  };

  const getTasksByKitStage = (stage: KitFrameStage): ProjectTask[] => {
    // For kit frames, we map departments to stages or use a custom field
    // procurement = design phase (planning/ordering)
    // production = production department
    // shipment = between production and installation
    // delivery = installation complete
    const stageToDepMap: Record<KitFrameStage, ProjectDepartment> = {
      'procurement': 'design',
      'production': 'production',
      'shipment': 'production', // Tasks marked for shipping
      'delivery': 'installation',
    };
    
    return filteredTasks
      .filter(t => {
        // Check if task has kitStage property (custom field) or map from department
        const taskKitStage = (t as any).kitStage || stageToDepMap[stage];
        if ((t as any).kitStage) {
          return (t as any).kitStage === stage;
        }
        return t.department === stageToDepMap[stage];
      })
      .sort((a, b) => {
        const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      });
  };

  const getTasksByStatus = (status: TaskStatus): ProjectTask[] => {
    return filteredTasks
      .filter(t => t.status === status)
      .sort((a, b) => {
        const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      });
  };

  const handleOpenTaskModal = (task?: ProjectTask) => {
    if (task) {
      setEditingTask(task);
      setTaskForm({
        title: task.title,
        description: task.description || '',
        department: task.department,
        kitStage: (task as any).kitStage || 'procurement',
        priority: task.priority,
        assignedTo: task.assignedTo,
        startDate: new Date(task.startDate).toISOString().split('T')[0],
        endDate: new Date(task.endDate).toISOString().split('T')[0],
      });
    } else {
      setEditingTask(null);
      setTaskForm({
        title: '',
        description: '',
        department: 'design',
        kitStage: 'procurement',
        priority: 'medium',
        assignedTo: [],
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      });
    }
    setShowTaskModal(true);
  };

  const handleSaveTask = async () => {
    if (!taskForm.title.trim() || !selectedProjectId) return;

    try {
      if (editingTask?.id) {
        await updateProjectTask(editingTask.id, {
          title: taskForm.title,
          description: taskForm.description,
          department: taskForm.department,
          priority: taskForm.priority,
          assignedTo: taskForm.assignedTo,
          startDate: new Date(taskForm.startDate),
          endDate: new Date(taskForm.endDate),
        });
      } else {
        await createProjectTask({
          projectId: selectedProjectId,
          title: taskForm.title,
          description: taskForm.description,
          department: taskForm.department,
          priority: taskForm.priority,
          assignedTo: taskForm.assignedTo,
          status: 'pending',
          startDate: new Date(taskForm.startDate),
          endDate: new Date(taskForm.endDate),
          dependencies: [],
          progress: 0,
          position: Date.now(),
        });
      }
      setShowTaskModal(false);
    } catch (err) {
      console.error('Error saving task:', err);
    }
  };

  const handleStatusChange = async (task: ProjectTask, newStatus: TaskStatus) => {
    if (!task.id) return;
    try {
      await updateProjectTask(task.id, { 
        status: newStatus,
        progress: newStatus === 'completed' ? 100 : task.progress,
      });
    } catch (err) {
      console.error('Error updating task status:', err);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await deleteProjectTask(taskId);
    } catch (err) {
      console.error('Error deleting task:', err);
    }
  };

  const renderTaskCard = (task: ProjectTask) => {
    const project = projects.find(p => p.id === task.projectId);
    const daysUntilDue = Math.ceil(
      (new Date(task.endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
    );
    const isOverdue = daysUntilDue < 0 && task.status !== 'completed';

    return (
      <div 
        key={task.id} 
        className={`task-card ${isOverdue ? 'overdue' : ''}`}
        onClick={() => handleOpenTaskModal(task)}
      >
        <div 
          className="task-priority-bar"
          style={{ backgroundColor: PRIORITY_COLORS[task.priority] }}
        />
        <div className="task-content">
          <div className="task-header">
            <span className="task-title">{task.title}</span>
            {task.priority === 'urgent' && <span className="urgent-badge">!</span>}
          </div>
          {project && !selectedProjectId && (
            <span className="task-project">{project.name}</span>
          )}
          <div className="task-meta">
            <span 
              className="task-department"
              style={{ backgroundColor: DEPARTMENT_COLORS[task.department] }}
            >
              {DEPARTMENT_DISPLAY_LABELS[task.department]}
            </span>
            <span className={`task-due ${isOverdue ? 'overdue' : ''}`}>
              {isOverdue ? `${Math.abs(daysUntilDue)}d overdue` : `${daysUntilDue}d left`}
            </span>
          </div>
          {task.progress > 0 && task.progress < 100 && (
            <div className="task-progress">
              <div 
                className="task-progress-fill" 
                style={{ width: `${task.progress}%` }}
              />
            </div>
          )}
        </div>
        <div className="task-actions" onClick={e => e.stopPropagation()}>
          <select
            value={task.status}
            onChange={(e) => handleStatusChange(task, e.target.value as TaskStatus)}
          >
            {TASK_STATUSES.map(status => (
              <option key={status} value={status}>
                {status.replace('-', ' ')}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  };

  const departmentStats = (department: ProjectDepartment) => {
    const deptTasks = getTasksByDepartment(department);
    const completed = deptTasks.filter(t => t.status === 'completed').length;
    return { total: deptTasks.length, completed };
  };

  const kitStageStats = (stage: KitFrameStage) => {
    const stageTasks = getTasksByKitStage(stage);
    const completed = stageTasks.filter(t => t.status === 'completed').length;
    return { total: stageTasks.length, completed };
  };

  const statusStats = (status: TaskStatus) => {
    const statusTasks = getTasksByStatus(status);
    return statusTasks.length;
  };

  if (!selectedProjectId && projects.length > 0) {
    return (
      <div className="project-board select-project">
        <div className="select-project-message">
          <h3>Select a Project</h3>
          <p>Choose a project from the dropdown above to view and manage tasks</p>
        </div>
        <div className="projects-quick-list">
          {projects.slice(0, 5).map(project => (
            <div key={project.id} className="project-quick-card">
              <span className="project-quick-name">{project.name}</span>
              <span className="project-quick-status">{project.status}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="project-board">
      {/* Board Header */}
      <div className="board-header">
        <div className="project-type-badge">
          {selectedProject && (
            <span className={`frame-type-badge ${selectedProject.frameType || 'custom'}`}>
              {selectedProject.frameType === 'kit' ? <><Package size={16} /> Kit Frame</> : <><Home size={16} /> Custom Frame</>}
            </span>
          )}
        </div>
        <div className="view-toggle-mini">
          {isKitFrame ? (
            <>
              <button 
                className={viewBy === 'kit-stage' ? 'active' : ''}
                onClick={() => setViewBy('kit-stage')}
              >
                By Stage
              </button>
              <button 
                className={viewBy === 'status' ? 'active' : ''}
                onClick={() => setViewBy('status')}
              >
                By Status
              </button>
            </>
          ) : (
            <>
              <button 
                className={viewBy === 'department' ? 'active' : ''}
                onClick={() => setViewBy('department')}
              >
                By Department
              </button>
              <button 
                className={viewBy === 'status' ? 'active' : ''}
                onClick={() => setViewBy('status')}
              >
                By Status
              </button>
            </>
          )}
        </div>
        <button className="add-task-btn" onClick={() => handleOpenTaskModal()}>
          + Add Task
        </button>
      </div>

      {/* Kanban Board */}
      <div className="board-columns">
        {viewBy === 'kit-stage' ? (
          // Kit Frame Stages: Procurement → Production → Shipment → Delivery
          KIT_STAGES.map(stage => {
            const stats = kitStageStats(stage);
            const columnTasks = getTasksByKitStage(stage);
            const stageLabels: Record<KitFrameStage, React.ReactNode> = {
              procurement: <><ClipboardList size={16} /> Procurement</>,
              production: <><Settings size={16} /> Production</>,
              shipment: <><Truck size={16} /> Shipment</>,
              delivery: <><CheckCircle size={16} /> Delivery</>
            };
            
            return (
              <div key={stage} className="board-column">
                <div 
                  className="column-header"
                  style={{ borderTopColor: KIT_STAGE_COLORS[stage] }}
                >
                  <div className="column-title">
                    <span className="column-name">{stageLabels[stage]}</span>
                    <span className="column-count">{stats.total}</span>
                  </div>
                  <span className="column-progress">
                    {stats.completed}/{stats.total} done
                  </span>
                </div>
                <div className="column-tasks">
                  {columnTasks.map(task => renderTaskCard(task))}
                  {columnTasks.length === 0 && (
                    <div className="no-tasks">No tasks</div>
                  )}
                </div>
              </div>
            );
          })
        ) : viewBy === 'department' ? (
          // Custom Frame Departments: Design → Production → Installation
          DEPARTMENTS.map(department => {
            const stats = departmentStats(department);
            const columnTasks = getTasksByDepartment(department);
            
            return (
              <div key={department} className="board-column">
                <div 
                  className="column-header"
                  style={{ borderTopColor: DEPARTMENT_COLORS[department] }}
                >
                  <div className="column-title">
                    <span className="column-name">{DEPARTMENT_DISPLAY_LABELS[department]}</span>
                    <span className="column-count">{stats.total}</span>
                  </div>
                  <span className="column-progress">
                    {stats.completed}/{stats.total} done
                  </span>
                </div>
                <div className="column-tasks">
                  {columnTasks.map(task => renderTaskCard(task))}
                  {columnTasks.length === 0 && (
                    <div className="no-tasks">No tasks</div>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          // By Status view (works for both frame types)
          TASK_STATUSES.map(status => {
            const count = statusStats(status);
            const columnTasks = getTasksByStatus(status);
            
            return (
              <div key={status} className="board-column">
                <div className="column-header">
                  <div className="column-title">
                    <span className="column-name">{status.replace('-', ' ')}</span>
                    <span className="column-count">{count}</span>
                  </div>
                </div>
                <div className="column-tasks">
                  {columnTasks.map(task => renderTaskCard(task))}
                  {columnTasks.length === 0 && (
                    <div className="no-tasks">No tasks</div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Task Modal */}
      {showTaskModal && (
        <div className="task-modal-overlay" onClick={() => setShowTaskModal(false)}>
          <div className="task-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingTask ? 'Edit Task' : 'New Task'}</h3>
              <button className="close-btn" onClick={() => setShowTaskModal(false)}>×</button>
            </div>
            <div className="modal-content">
              <div className="form-row">
                <label>Title</label>
                <input
                  type="text"
                  value={taskForm.title}
                  onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                  placeholder="Task title"
                />
              </div>
              <div className="form-row">
                <label>Description</label>
                <textarea
                  value={taskForm.description}
                  onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                  placeholder="Task description"
                  rows={3}
                />
              </div>
              <div className="form-row-group">
                <div className="form-row">
                  <label>Department</label>
                  <select
                    value={taskForm.department}
                    onChange={(e) => setTaskForm({ ...taskForm, department: e.target.value as ProjectDepartment })}
                  >
                    {DEPARTMENTS.map(d => (
                      <option key={d} value={d}>{DEPARTMENT_DISPLAY_LABELS[d]}</option>
                    ))}
                  </select>
                </div>
                <div className="form-row">
                  <label>Priority</label>
                  <select
                    value={taskForm.priority}
                    onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value as TaskPriority })}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>
              <div className="form-row-group">
                <div className="form-row">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={taskForm.startDate}
                    onChange={(e) => setTaskForm({ ...taskForm, startDate: e.target.value })}
                  />
                </div>
                <div className="form-row">
                  <label>End Date</label>
                  <input
                    type="date"
                    value={taskForm.endDate}
                    onChange={(e) => setTaskForm({ ...taskForm, endDate: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              {editingTask && (
                <button 
                  className="delete-btn"
                  onClick={() => editingTask.id && handleDeleteTask(editingTask.id)}
                >
                  Delete
                </button>
              )}
              <div className="footer-right">
                <button onClick={() => setShowTaskModal(false)}>Cancel</button>
                <button className="save-btn" onClick={handleSaveTask}>
                  {editingTask ? 'Save Changes' : 'Create Task'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectBoard;
